import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { Box } from "@mui/material";
import { useMyHrUserRolesDataByContract, useMyHrEmployee } from "my-hr/hooks";
import {
  MyTimesheetMyRequestsDashboard,
  MyTimesheetMyRequestsDashboardPrint,
  MyTimesheetPageTitle,
  MyTimesheetWorktimeReport,
  MyTimesheetWorktimeSummary,
} from "my-timesheet/components";
import {
  useMyTimesheetDashboardDate,
  useMyTimesheetTabsState,
  useMyTimesheetTimesheetList,
} from "my-timesheet/hooks";
import { OcPageTransition, OcTabPanel } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import useOcButtonGroup from "oc/hooks/use-button-group";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFullname } from "oc/utils";

const useMySheetRoleSelector = () => {
  const buttonsDef = ["MY_TIMESHEET_EXAMINER_VIEW", "MY_TIMESHEET_VIEWER_VIEW"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MySheetMyRequestsDashboardRoleSelector",
    buttonsDef,
  });

  let { contractCode } = useOcCurrentUserState();

  const { roles } = useMyHrUserRolesDataByContract({
    contract: contractCode,
  });

  const isExaminer = roles.includes("MyTimesheet.examiner");
  const isViewer = roles.includes("MyTimesheet.viewer");
  const showComponent = isExaminer && isViewer;

  let role =
    selectedButton === "MY_TIMESHEET_EXAMINER_VIEW" ? "examiner" : "viewer";
  if (!isExaminer && isViewer) {
    role = "viewer";
  }

  return { OcButtonGroup, role, showComponent };
};

export const MyTimesheetExaminerDashboardPage = () => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();
  const componentRef = useRef(null);

  const { activeTab, MySheetOcTabs } = useMyTimesheetTabsState({
    id: "examinerDashboardTab",
    tabsDef: [
      "MY_TIMESHEET_OWN_WORKIME",
      "MY_TIMESHEET_WORKTIME_SUMMARY",
      "MY_TIMESHEET_WORKTIME_REPORT",
    ],
  });

  const { OcButtonGroup, showComponent, role } = useMySheetRoleSelector();

  const {
    currentDate,
    period,
    handlePeriodChange,
    handleChangeCurrentDate,
    periodStart,
    periodEnd,
  } = useMyTimesheetDashboardDate("examinerDashboard");

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });

  const { hoursSummary, extendedData } = useMyTimesheetTimesheetList({
    contractId: contractCode,
    periodStart: periodStart,
    periodEnd: periodEnd,
    dashboard: true,
    role: "user",
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <OcPageTransition>
      <Box padding={isMobile ? "0px" : "16px"}>
        <MyTimesheetPageTitle
          title={t("MY_TIMESHEET_SUMMARY")}
          showDateRange={true}
          currentDate={currentDate}
          handleChangeCurrentDate={handleChangeCurrentDate}
          period={period === "week" || period === "month" ? period : undefined}
          handlePeriodChange={(p: string) =>
            handlePeriodChange(p as "week" | "month")
          }
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Box>
          <MySheetOcTabs />
          <Box height="16px" />
          {activeTab !== 0 && showComponent && (
            <>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                padding="6px 0px"
              >
                <OcButtonGroup />
              </Box>
            </>
          )}
          <Box height="16px" />
          <OcTabPanel value={activeTab} index={0} padding="0px">
            <MyTimesheetMyRequestsDashboard
              periodStart={periodStart.toISOString()}
              periodEnd={periodEnd.toISOString()}
              isMobile={isMobile}
              onPrint={handlePrint}
            />
          </OcTabPanel>
          <OcTabPanel value={activeTab} index={1} padding="0px">
            <MyTimesheetWorktimeSummary
              handlePeriodChange={handlePeriodChange}
              periodStart={periodStart}
              periodEnd={periodEnd}
              period={period === "week" || period === "month" ? period : "week"}
              role={role}
              isMobile={isMobile}
            />
          </OcTabPanel>
          <OcTabPanel value={activeTab} index={2} padding="0px">
            <MyTimesheetWorktimeReport
              periodStart={periodStart.toISOString()}
              periodEnd={periodEnd.toISOString()}
              role={role}
              isMobile={isMobile}
            />
          </OcTabPanel>
        </Box>
      </Box>
      <Box style={{ display: "none" }}>
        <MyTimesheetMyRequestsDashboardPrint
          ref={componentRef}
          periodStart={periodStart.toISOString()}
          employeeName={getFullname(employeeData) ?? ""}
          contractCode={contractCode}
          hoursSummary={hoursSummary}
          dailyEntries={extendedData || []}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyTimesheetExaminerDashboardPage;
